import { useQueryParams } from '@calendly/hooks'
import { SafeSuspense } from '@calendly/safe-suspense'
import { Modal } from '@calendly/ui/components/modal'
import PropTypes from 'prop-types'
import { lazy, useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Segment } from 'core/analytics/segment'
import changeSet from 'core/change_set'

import { BOOKING_METHODS } from '../constants'
import {
  bookingMethodSelector,
  currentTabSelector,
  editingSelector,
  modalDirtySelector,
  modalVisibleSelector,
  submitErrorSelector
} from '../selectors'
import { closeModal, openModal, resetSubmitError, stepList } from '../slice'
import { Banner } from './banner'
import { Navigator } from './navigator'
import { SkeletonLoading } from './skeleton_loading'
import { ModalContent } from './styles'

const loadChunk = async () => {
  const loaded = await import(/* webpackChunkName: 'one_off_modal' */'./modal_body')
  return { default: loaded.ModalBody }
}

const ModalBody = lazy(loadChunk)

export const OneOffModal = ({ onClose = () => {} }) => {
  const ref = useRef()
  const dispatch = useDispatch()
  const editing = useSelector(editingSelector)
  const modalVisible = useSelector(modalVisibleSelector)
  const modalDirty = useSelector(modalDirtySelector)
  const currentTab = useSelector(currentTabSelector)
  const { title: errorTitle, body: errorBody } = useSelector(submitErrorSelector)
  const bookingMethod = useSelector(bookingMethodSelector)
  const showErrorBanner = !!errorBody
  const { create_new_meeting: createNewMeeting } = useQueryParams()
  const paramBookingMethod = BOOKING_METHODS[createNewMeeting]

  const handleClose = useCallback(() => {
    const proceedWithClose = !modalDirty || window.confirm(changeSet.getMessage())

    if (!proceedWithClose) {
      return false
    }

    dispatch(closeModal())
    changeSet.reset()
    onClose()
    return true
  }, [dispatch, modalDirty, onClose])

  const onBannerClose = () => dispatch(resetSubmitError())
  const stepName = stepList[currentTab]

  useEffect(() => {
    if (paramBookingMethod) {
      dispatch(openModal({ bookingMethod: paramBookingMethod }))
    } else {
      dispatch(closeModal())
    }
  }, [dispatch, paramBookingMethod])

  useEffect(() => {
    if (modalVisible) {
      const trackingEventType = (bookingMethod === BOOKING_METHODS.instant)
        ? 'upgraded_one_off_meeting.form_opened'
        : 'poll.form_opened'
      Segment.track(trackingEventType)
    }
  }, [bookingMethod, modalVisible])

  return (
    <Modal ref={ref} isOpen={modalVisible} onClose={handleClose}>
      <Banner
        show={showErrorBanner}
        title={errorTitle}
        body={errorBody}
        onClose={onBannerClose}
        status="error"
      />
      <ModalContent>
        <Navigator
          stepName={stepName}
          bookingMethod={bookingMethod}
          currentTab={currentTab}
          editing={editing}
          onClose={handleClose}
        />
        <SafeSuspense fallback={<SkeletonLoading />}>
          <ModalBody modalRef={ref} onClose={onClose} />
        </SafeSuspense>
      </ModalContent>
    </Modal>
  )
}

OneOffModal.propTypes = {
  onClose: PropTypes.func,
}
